import { BasketballEvent, SelectOption } from '@hefe/models';
import {
  BasketballPracticesSaveRequest,
  BasketballPracticesSlice,
  BasketballPracticesUpdateRequest,
  BasketballTeamsSlice,
  currentUserIsAdminSelector,
  GymsSlice,
  ModalsSlice,
  userActiveBasketballCoachTeamId,
  userState,
} from '@hefe/store';
import { Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { t } from '@hefe/i18n';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm, {
  FormField,
} from '../../../core/components/form/custom-form';

interface EditPracticesFormProps {
  practiceIds: number[];
  practice: BasketballEvent;
}

export default function EditPracticesForm(props: EditPracticesFormProps) {
  const dispatch = useAppDispatch();

  const [refresh, doRefresh] = useState(false);
  const [gym, setGym] = useState(true);

  const stateUser = useSelector(userState);
  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const gyms = useSelector(GymsSlice.gymsSelectSelector);
  const activeCoachTeamId = useSelector(userActiveBasketballCoachTeamId);
  const teams = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  useEffect(() => {
    doRefresh((prev) => !prev);
  }, [activeCoachTeamId]);

  const formKey = `edit-practices-form`;

  const onSubmit = (values: any, resetForm: any) => {
    const setLoading = ModalsSlice.modalsActions.setModalLoading;
    const close = ModalsSlice.modalsActions.closeModal;

    const gymValue: any = {};
    if (gym) {
      gymValue['gym_id'] = values.gym_id;
    } else {
      gymValue['location'] = values.location;
    }

    const submitData: BasketballPracticesUpdateRequest = {
      ...values,
      ...gymValue,
      ids: props.practiceIds,
      time: values.time ? dayjs(values.time).format('HH:mm') : undefined,
    };

    dispatch(setLoading(true));
    dispatch(BasketballPracticesSlice.updateBasketballPractices(submitData))
      .unwrap()
      .then(() => dispatch(close()))
      .catch(() => null)
      .finally(() => dispatch(setLoading(false)));
  };

  const gymField = (): FormField => {
    if (!gym) {
      return {
        name: 'location',
        label: t('location'),
        type: 'text',
        rules: [],
      };
    } else {
      return {
        name: 'gym_id',
        label: t('gyms.singular'),
        type: 'select',
        selectOptions: gyms,
        rules: [],
      };
    }
  };

  const teamsField = (): FormField => {
    const selectOptions = teams.map((team) =>
      team.value === activeCoachTeamId ? { ...team, disabled: true } : team
    );
    let initialValue = [];
    if (activeCoachTeamId) {
      initialValue.push(activeCoachTeamId);
    }

    initialValue = [...new Set(initialValue)];

    return {
      name: 'team_ids',
      label: t('teams'),
      type: 'select',
      selectOptions: selectOptions,
      multiSelect: true,
      disableSelectClear: true,
      initalValue: initialValue,
      rules: [],
    };
  };

  const weekdayField = (): FormField => {
    const weekdays: SelectOption[] = [];

    for (let i = 0; i < 7; i++) {
      weekdays.push({
        label: dayjs().day(i).format('dddd'),
        value: i,
      });
    }

    return {
      name: 'weekday',
      label: t('weekday'),
      type: 'select',
      selectOptions: weekdays,
      rules: [],
    };
  };

  return (
    <Space direction="vertical">
      <Typography.Paragraph>
        {t('information_texts.edit_practices')}
      </Typography.Paragraph>
      <CustomForm
        key={formKey}
        formId={formKey}
        refresh={refresh}
        fields={[
          {
            name: 'time',
            label: t('time'),
            type: 'time',
            rules: [],
          },
          {
            name: 'in_gym',
            label: t('gyms.singular') + '?',
            type: 'checkbox',
            value: gym,
            initalValue: gym,
            onChange: (value) => setGym(value),
            rules: [],
          },
          { ...gymField() },
          {
            name: 'description',
            label: t('note'),
            type: 'text',
            rules: [],
          },
          {
            name: 'duration',
            label: `${t('duration')} (${t('in_min')})`,
            type: 'number',
            rules: [
              {
                type: 'number',
                max: 1440,
                message: t('form_errors.number_max', { max: 1440 }),
              },
              {
                type: 'number',
                min: 1,
                message: t('form_errors.number_min', { min: 1 }),
              },
            ],
          },
          {
            name: 'limit',
            label: t('limit'),
            type: 'number',
            rules: [],
          },
          {
            name: 'canceled',
            label: t('canceled'),
            type: 'checkbox',
            rules: [],
          },
        ]}
        onSubmit={onSubmit}
      />
    </Space>
  );
}
